import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Zap, DollarSign, Cpu, BarChart3,
  Play, Pause, AlertTriangle,
  PlayCircle, RefreshCw, Bell,
  LineChart as LineChartIcon
} from 'lucide-react';
import { XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';

import {
  Pipeline, AgentCard, MarketPrices, IntangiHubOrders, IntangiHubChart,
  PortfolioSimulator, MyPortfolio, MarketTabSelector,
  useWebSocket, TickerBar, LiveToasts, QuickStartGuide,
} from '../components/dashboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [stats, setStats] = useState(null);
  const [performance, setPerformance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tradingStatus, setTradingStatus] = useState(null);
  const [alerts, setAlerts] = useState({ recent: [], unread: 0 });
  const [activeMarket, setActiveMarket] = useState('crypto');

  const { data: wsData, connected: wsConnected, notifications, clearNotifications } = useWebSocket(BACKEND_URL);
  const [showNotifPanel, setShowNotifPanel] = useState(false);

  const [cryptoData, setCryptoData] = useState({});
  const [stockData, setStockData] = useState({});
  const [etfData, setEtfData] = useState({});
  const [forexData, setForexData] = useState({});
  const [intangiData, setIntangiData] = useState({});
  const [marketLoading, setMarketLoading] = useState(false);

  const [intangiOrders, setIntangiOrders] = useState(null);
  const [intangiChart, setIntangiChart] = useState(null);
  const [intangiSymbols, setIntangiSymbols] = useState([]);
  const [selectedIpSymbol, setSelectedIpSymbol] = useState('IPIX/USD');
  const [intangiOrdersLoading, setIntangiOrdersLoading] = useState(false);
  const [intangiChartLoading, setIntangiChartLoading] = useState(false);

  const fetchCoreData = useCallback(async () => {
    try {
      const [agentsRes, statsRes, perfRes, tradingRes, alertsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/agents`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/stats`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/performance`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/trading/status`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/alerts/summary`, { withCredentials: true }).catch(() => ({ data: { recent: [], unread: 0 } }))
      ]);
      setAgents(agentsRes.data);
      setStats(statsRes.data);
      setPerformance(perfRes.data);
      setTradingStatus(tradingRes.data);
      setAlerts(alertsRes.data);
    } catch (err) {
      console.error('Error fetching core data:', err);
    }
    setLoading(false);
  }, []);

  const fetchMarketData = useCallback(async (market) => {
    setMarketLoading(true);
    try {
      switch (market) {
        case 'crypto': {
          const res = await axios.get(`${BACKEND_URL}/api/crypto/prices`, { withCredentials: true });
          const mapped = {};
          if (Array.isArray(res.data)) {
            res.data.forEach(c => { mapped[c.symbol || c.id] = { price: c.price_usd, change_24h: c.change_24h, source: 'coingecko' }; });
          } else { Object.assign(mapped, res.data); }
          setCryptoData(mapped);
          break;
        }
        case 'stocks': {
          const res = await axios.get(`${BACKEND_URL}/api/market/stocks`, { withCredentials: true });
          setStockData(res.data);
          break;
        }
        case 'etfs': {
          const res = await axios.get(`${BACKEND_URL}/api/market/etfs`, { withCredentials: true });
          setEtfData(res.data);
          break;
        }
        case 'forex': {
          const res = await axios.get(`${BACKEND_URL}/api/market/forex`, { withCredentials: true });
          setForexData(res.data);
          break;
        }
        case 'intangibles': {
          const res = await axios.get(`${BACKEND_URL}/api/market/intangibles`, { withCredentials: true });
          setIntangiData(res.data);
          break;
        }
        default: break;
      }
    } catch (err) {
      console.error(`Error fetching ${market} data:`, err);
    }
    setMarketLoading(false);
  }, []);

  const fetchIntangiHub = useCallback(async () => {
    setIntangiOrdersLoading(true);
    setIntangiChartLoading(true);
    try {
      const [ordersRes, productsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/intangi/orders`, { withCredentials: true }).catch(() => ({ data: { orders: [], total: 0 } })),
        axios.get(`${BACKEND_URL}/api/intangi/products`, { withCredentials: true }).catch(() => ({ data: { products: [] } })),
      ]);
      setIntangiOrders(ordersRes.data);
      const products = productsRes.data?.products || [];
      const syms = products.map(p => ({ symbol: p.symbol || p.id, name: p.name, product_id: p.id, type: p.product_type, price: p.price }));
      setIntangiSymbols(syms.length > 0 ? syms : []);
      if (syms.length > 0 && !selectedIpSymbol) {
        setSelectedIpSymbol(syms[0].symbol || 'IPIX100');
      }
    } catch (err) {
      console.error('IntangiHub fetch error:', err);
    }
    setIntangiOrdersLoading(false);

    try {
      const chartRes = await axios.get(`${BACKEND_URL}/api/intangi/chart/${selectedIpSymbol.replace('/', '-')}`, {
        params: { interval: '1h', limit: 50 },
        withCredentials: true
      });
      setIntangiChart(chartRes.data);
    } catch (err) {
      console.error('IntangiHub chart error:', err);
    }
    setIntangiChartLoading(false);
  }, [selectedIpSymbol]);

  useEffect(() => {
    fetchCoreData();
    fetchMarketData('crypto');
    const interval = setInterval(fetchCoreData, 30000);
    return () => clearInterval(interval);
  }, [fetchCoreData, fetchMarketData]);

  useEffect(() => {
    fetchMarketData(activeMarket);
    if (activeMarket === 'intangibles') {
      fetchIntangiHub();
    }
  }, [activeMarket, fetchMarketData, fetchIntangiHub]);

  const getActiveMarketData = () => {
    switch (activeMarket) {
      case 'crypto': return cryptoData;
      case 'stocks': return stockData;
      case 'etfs': return etfData;
      case 'forex': return forexData;
      case 'intangibles': return intangiData;
      default: return {};
    }
  };

  const handleToggleAgent = async (agentId, newStatus) => {
    try {
      await axios.put(`${BACKEND_URL}/api/agents/${agentId}`, { status: newStatus }, { withCredentials: true });
      setAgents(prev => prev.map(a => a.id === agentId ? { ...a, status: newStatus } : a));
    } catch (err) { console.error('Error toggling agent:', err); }
  };

  const startTradingEngine = async () => {
    try { await axios.post(`${BACKEND_URL}/api/trading/start`, {}, { withCredentials: true }); fetchCoreData(); }
    catch (err) { console.error('Error starting trading engine:', err); }
  };

  const runTradingCycle = async () => {
    try { await axios.post(`${BACKEND_URL}/api/trading/cycle`, { cycles: 1 }, { withCredentials: true }); fetchCoreData(); }
    catch (err) { console.error('Error running trading cycle:', err); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030508] flex items-center justify-center">
        <div className="text-center">
          <Zap className="w-12 h-12 text-emerald-500 animate-pulse mx-auto mb-4" />
          <p className="text-slate-400 font-mono">{t('dashboard.loading')}</p>
        </div>
      </div>
    );
  }

  const liveAgents = agents.filter(a => a.status === 'LIVE').length;

  return (
    <div data-testid="dashboard">
      {/* Slim Top Toolbar */}
      <div className="sticky top-0 z-30 bg-[#030508]/90 backdrop-blur-xl border-b border-white/[0.08]">
        <div className="px-4 md:px-6 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-xs">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-slate-400 font-mono">{t('dashboard.systemActive')}</span>
              <span className="text-emerald-400 font-mono">{liveAgents} agents</span>
            </div>
            <button onClick={startTradingEngine}
              className="flex items-center gap-1 px-3 py-1.5 bg-emerald-500/20 text-emerald-400 text-xs font-mono hover:bg-emerald-500/30 transition-colors"
              data-testid="start-engine-btn">
              <PlayCircle className="w-4 h-4" /> START
            </button>
            <button onClick={runTradingCycle}
              className="flex items-center gap-1 px-3 py-1.5 bg-blue-500/20 text-blue-400 text-xs font-mono hover:bg-blue-500/30 transition-colors"
              data-testid="run-cycle-btn">
              <RefreshCw className="w-4 h-4" /> CYCLE
            </button>
          </div>
          <div className="relative">
            <button onClick={() => setShowNotifPanel(!showNotifPanel)} className="relative text-slate-400 hover:text-white" data-testid="alerts-btn">
              <Bell className="w-5 h-5" />
              {notifications.length > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 text-[10px] font-bold rounded-full flex items-center justify-center text-[#030508]">{notifications.length > 9 ? '9+' : notifications.length}</span>
              )}
            </button>
            {showNotifPanel && (
              <div className="absolute right-0 top-10 w-96 max-h-[480px] bg-[#090B0F] border border-white/10 z-50 shadow-2xl overflow-hidden" data-testid="notification-panel">
                <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                  <span className="text-xs uppercase tracking-[0.2em] font-semibold text-white">{t('dashboard.liveNotifications')}</span>
                  <button onClick={() => { clearNotifications(); setShowNotifPanel(false); }} className="text-xs text-slate-500 hover:text-white font-mono" data-testid="clear-notifications-btn">{t('common.clear')}</button>
                </div>
                <div className="overflow-y-auto max-h-[420px]">
                  {notifications.length > 0 ? notifications.map(n => (
                    <div key={n.id} className={`px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors ${
                      n.severity === 'success' ? 'border-l-2 border-l-emerald-500' :
                      n.severity === 'warning' ? 'border-l-2 border-l-amber-500' :
                      'border-l-2 border-l-blue-500'
                    }`}>
                      <div className="flex items-center justify-between mb-0.5">
                        <span className={`text-xs font-bold font-mono ${
                          n.type === 'trade' ? 'text-blue-400' :
                          n.type === 'milestone' ? 'text-emerald-400' :
                          n.type === 'agent' ? 'text-amber-400' :
                          'text-slate-300'
                        }`}>{n.title}</span>
                        <span className="text-[10px] text-slate-600 font-mono">{n.timestamp ? new Date(n.timestamp).toLocaleTimeString() : ''}</span>
                      </div>
                      <p className="text-xs text-slate-400">{n.message}</p>
                    </div>
                  )) : (
                    <div className="p-8 text-center text-slate-600 text-xs font-mono">
                      Esperando actividad del engine...
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <TickerBar wsData={wsData} connected={wsConnected} />

      <main className="p-4 md:p-6 space-y-6">
        <QuickStartGuide stats={stats} />

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="stats-overview">
          <div className="bg-[#090B0F] border border-white/10 p-4">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">{t('dashboard.pnlTotal')}</p>
            <p className={`text-2xl md:text-3xl font-black font-mono ${(stats?.total_pnl ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`} data-testid="stat-alpha">
              {(stats?.total_pnl ?? 0) >= 0 ? '+' : ''}${Math.abs(stats?.total_pnl ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </p>
          </div>
          <div className="bg-[#090B0F] border border-white/10 p-4">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">{t('dashboard.activeAgents')}</p>
            <p className="text-2xl md:text-3xl font-black text-white font-mono" data-testid="stat-agents">{stats?.live_agents ?? liveAgents}</p>
          </div>
          <div className="bg-[#090B0F] border border-white/10 p-4">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">{t('dashboard.tradingEngine')}</p>
            <p className={`text-2xl md:text-3xl font-black font-mono ${stats?.engine_running ? 'text-emerald-400' : 'text-slate-500'}`} data-testid="stat-engine">
              {stats?.engine_running ? 'ACTIVE' : tradingStatus?.status?.running ? 'ACTIVE' : 'IDLE'}
            </p>
          </div>
          <div className="bg-[#090B0F] border border-white/10 p-4">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">{t('dashboard.positions')}</p>
            <p className="text-2xl md:text-3xl font-black text-white font-mono">{stats?.open_positions ?? 0}</p>
          </div>
          <div className="bg-[#090B0F] border border-white/10 p-4">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">{t('dashboard.cycles')}</p>
            <p className="text-2xl md:text-3xl font-black text-white font-mono">{stats?.engine_cycles ?? 0}</p>
          </div>
        </div>

        <Pipeline />

        {/* Market Tabs + Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3 space-y-4">
            {/* Performance Chart */}
            <div className="bg-[#090B0F] border border-white/10 p-4 md:p-6" data-testid="performance-chart">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-emerald-500" />
                  <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{t('dashboard.portfolioPerformance')}</h3>
                </div>
                <div className="flex items-center gap-4 text-sm font-mono">
                  <span className="text-slate-400">P&L:</span>
                  <span className="text-emerald-400">
                    +${performance.length > 0 ? performance[performance.length - 1]?.cumulative_pnl?.toLocaleString() : 0}
                  </span>
                </div>
              </div>
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={performance}>
                    <defs>
                      <linearGradient id="colorPnl" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="date" axisLine={false} tickLine={false}
                      tick={{ fill: '#64748B', fontSize: 10 }}
                      tickFormatter={(v) => v.split('-').slice(1).join('/')} />
                    <YAxis axisLine={false} tickLine={false}
                      tick={{ fill: '#64748B', fontSize: 10 }}
                      tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`} />
                    <Tooltip contentStyle={{ backgroundColor: '#11141D', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 0 }}
                      labelStyle={{ color: '#94A3B8' }}
                      formatter={(v) => [`$${v?.toLocaleString()}`, 'Value']} />
                    <Area type="monotone" dataKey="portfolio_value" stroke="#10B981" strokeWidth={2}
                      fillOpacity={1} fill="url(#colorPnl)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <MarketTabSelector activeMarket={activeMarket} onSelect={setActiveMarket} />

            {activeMarket === 'intangibles' ? (
              <div className="space-y-4" data-testid="ip-assets-section">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <MarketPrices data={getActiveMarketData()} type={activeMarket} loading={marketLoading} />
                  <IntangiHubChart chartData={intangiChart} symbol={selectedIpSymbol} loading={intangiChartLoading} />
                </div>

                {intangiSymbols.length > 0 && (
                  <div className="flex gap-2 flex-wrap" data-testid="ip-symbol-selector">
                    {intangiSymbols.map(sym => (
                      <button key={sym.symbol} onClick={() => setSelectedIpSymbol(sym.symbol)}
                        className={`px-3 py-1.5 font-mono text-[10px] border transition-all ${selectedIpSymbol === sym.symbol
                          ? 'border-purple-500 text-purple-400 bg-purple-500/10'
                          : 'border-white/10 text-slate-500 hover:text-slate-300 hover:border-white/20'}`}
                        data-testid={`ip-symbol-btn-${sym.symbol}`}>
                        {sym.symbol}
                      </button>
                    ))}
                  </div>
                )}

                <IntangiHubOrders orders={intangiOrders} loading={intangiOrdersLoading} />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="market-data-grid">
                <MarketPrices data={getActiveMarketData()} type={activeMarket} loading={marketLoading} />
                <MarketSummary data={getActiveMarketData()} />
              </div>
            )}
          </div>

          <div className="space-y-6">
            <PortfolioSimulator />
            <MyPortfolio agents={agents} />
          </div>
        </div>

        {/* Agents Grid */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Cpu className="w-5 h-5 text-emerald-500" />
            <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{t('dashboard.agentNetwork')}</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="agents-grid">
            {agents.map(agent => (
              <AgentCard key={agent.id} agent={agent} onToggle={handleToggleAgent} />
            ))}
          </div>
        </div>

        {/* Capital Allocation Table */}
        <div className="bg-[#090B0F] border border-white/10 p-4 md:p-6" data-testid="capital-allocation">
          <div className="flex items-center gap-2 mb-4">
            <DollarSign className="w-5 h-5 text-emerald-500" />
            <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{t('dashboard.capitalAllocation')}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">{t('common.agent')}</th>
                  <th className="text-left py-3 px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">{t('common.type')}</th>
                  <th className="text-right py-3 px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">{t('common.capital')}</th>
                  <th className="text-right py-3 px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">P&L</th>
                  <th className="text-right py-3 px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">{t('common.return')}</th>
                  <th className="text-right py-3 px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">{t('common.sharpe')}</th>
                  <th className="text-right py-3 px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">{t('common.winRate')}</th>
                  <th className="text-center py-3 px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">{t('common.status')}</th>
                </tr>
              </thead>
              <tbody>
                {agents.map(agent => (
                  <tr key={agent.id} className="border-b border-white/5 hover:bg-[#11141D] transition-colors">
                    <td className="py-3 px-2">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-slate-400 text-xs">{agent.id}</span>
                        <span className="text-white font-medium text-xs">{agent.name}</span>
                      </div>
                    </td>
                    <td className="py-3 px-2 font-mono text-xs text-slate-400">{agent.type}</td>
                    <td className="py-3 px-2 text-right font-mono text-white text-xs">${agent.capital_allocated?.toLocaleString()}</td>
                    <td className={`py-3 px-2 text-right font-mono text-xs ${agent.current_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {agent.current_pnl >= 0 ? '+' : ''}${agent.current_pnl?.toLocaleString()}
                    </td>
                    <td className={`py-3 px-2 text-right font-mono text-xs ${agent.total_return >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {agent.total_return >= 0 ? '+' : ''}{agent.total_return}%
                    </td>
                    <td className="py-3 px-2 text-right font-mono text-white text-xs">{agent.sharpe_ratio}</td>
                    <td className="py-3 px-2 text-right font-mono text-white text-xs">{agent.win_rate}%</td>
                    <td className="py-3 px-2 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono ${agent.status === 'LIVE' ? 'text-emerald-400 bg-emerald-400/10' :
                        agent.status === 'WATCHDOG' ? 'text-amber-400 bg-amber-400/10' :
                          'text-slate-400 bg-slate-400/10'}`}>
                        {agent.status === 'LIVE' && <Play className="w-3 h-3" />}
                        {agent.status === 'WATCHDOG' && <AlertTriangle className="w-3 h-3" />}
                        {agent.status === 'OFF' && <Pause className="w-3 h-3" />}
                        {agent.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      <LiveToasts notifications={notifications} />
    </div>
  );
}

function MarketSummary({ data }) {
  const { t } = useTranslation();
  const entries = Object.entries(data || {});
  if (entries.length === 0) return (
    <div className="bg-[#090B0F] border border-white/10 p-4" data-testid="market-summary">
      <div className="flex items-center gap-2 mb-4">
        <LineChartIcon className="w-5 h-5 text-slate-400" />
        <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{t('dashboard.marketSummary')}</h3>
      </div>
      <p className="text-slate-500 text-xs font-mono py-4">{t('common.loading')}...</p>
    </div>
  );

  const gainers = entries.filter(([, d]) => (d.change_24h ?? 0) > 0).length;
  const losers = entries.length - gainers;
  const topGainer = entries.reduce((a, b) => ((b[1].change_24h ?? 0) > (a[1].change_24h ?? 0) ? b : a), entries[0]);
  const topLoser = entries.reduce((a, b) => ((b[1].change_24h ?? 0) < (a[1].change_24h ?? 0) ? b : a), entries[0]);

  return (
    <div className="bg-[#090B0F] border border-white/10 p-4" data-testid="market-summary">
      <div className="flex items-center gap-2 mb-4">
        <LineChartIcon className="w-5 h-5 text-slate-400" />
        <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{t('dashboard.marketSummary')}</h3>
      </div>
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-xs text-slate-500">{t('dashboard.assetsTracked')}</span>
          <span className="font-mono text-white">{entries.length}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-xs text-slate-500">{t('dashboard.gainersLosers')}</span>
          <span className="font-mono">
            <span className="text-emerald-400">{gainers}</span>
            <span className="text-slate-500"> / </span>
            <span className="text-red-400">{losers}</span>
          </span>
        </div>
        <div className="border-t border-white/5 pt-3">
          <p className="text-xs text-slate-500 mb-1">{t('dashboard.topPerformer')}</p>
          <div className="flex justify-between">
            <span className="font-mono text-emerald-400 text-sm">{topGainer?.[0]}</span>
            <span className="font-mono text-emerald-400 text-sm">+{(topGainer?.[1]?.change_24h ?? 0).toFixed(2)}%</span>
          </div>
        </div>
        <div className="border-t border-white/5 pt-3">
          <p className="text-xs text-slate-500 mb-1">{t('dashboard.worstPerformer')}</p>
          <div className="flex justify-between">
            <span className="font-mono text-red-400 text-sm">{topLoser?.[0]}</span>
            <span className="font-mono text-red-400 text-sm">{(topLoser?.[1]?.change_24h ?? 0).toFixed(2)}%</span>
          </div>
        </div>
      </div>
    </div>
  );
}
